import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Card, CardActionArea, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { CheckCircle2, Lock, LogOut, PlayCircle } from 'lucide-react';
import { getRequiredVideoUrl, postRequiredVideoProgress } from '../api/api';

// Envoi de la progression pendant la lecture : assez fréquent pour que le serveur, qui borne la progression au
// temps réel écoulé (requiredVideosService.nextMaxWatched), suive sans jamais refuser une lecture normale.
const PROGRESS_EVERY_MS = 10 * 1000;
// Petit saut toléré entre deux timeupdate (lecture continue) ; au-delà, c'est une avance rapide.
const CONTINUOUS_STEP_SEC = 2;

const fmt = (sec) => `${Math.floor(sec / 60)} min ${String(Math.round(sec % 60)).padStart(2, '0')}`;

/**
 * Écran « Vidéos à voir » affiché à l'ouverture d'opé tant qu'une vidéo obligatoire n'a pas été vue jusqu'au bout
 * (tous les rôles sauf admin, cf. api routes/requiredVideos.ts). Pas d'avance rapide : on peut mettre en pause et
 * revenir en arrière, jamais aller au-delà de ce qui a déjà été vu ; la progression est gardée côté serveur.
 */
const RequiredVideosGate = ({ initialVideos, onDone, onLogout }) => {
  const [videos, setVideos] = useState(initialVideos);
  const firstPending = videos.find((v) => !v.completed) || videos[0];
  const [currentKey, setCurrentKey] = useState(firstPending?.key);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const maxRef = useRef(0);
  const lastSentRef = useRef(-1);

  const current = videos.find((v) => v.key === currentKey);
  const allDone = videos.every((v) => v.completed);

  // Lien signé de la vidéo courante (redemandé à chaque changement de vidéo).
  useEffect(() => {
    if (!currentKey) return undefined;
    let cancelled = false;
    setUrl(null);
    setError(null);
    maxRef.current = videos.find((v) => v.key === currentKey)?.maxWatchedSec || 0;
    lastSentRef.current = -1;
    getRequiredVideoUrl(currentKey)
      .then((data) => { if (!cancelled) setUrl(data.url); })
      .catch(() => { if (!cancelled) setError('Impossible de charger la vidéo. Réessayez dans un instant.'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seul le changement de vidéo relance le chargement
  }, [currentKey]);

  const sendProgress = useCallback(async (position) => {
    if (!currentKey || position <= lastSentRef.current) return;
    lastSentRef.current = position;
    try {
      const res = await postRequiredVideoProgress(currentKey, position);
      setVideos((prev) => prev.map((v) => (v.key === currentKey
        ? { ...v, maxWatchedSec: res.maxWatchedSec, completed: v.completed || res.completed }
        : v)));
    } catch (err) {
      console.error('Erreur enregistrement progression vidéo:', err);
    }
  }, [currentKey]);

  useEffect(() => {
    const timer = setInterval(() => {
      const el = videoRef.current;
      if (el && !el.paused) sendProgress(maxRef.current);
    }, PROGRESS_EVERY_MS);
    const onHide = () => { if (document.visibilityState === 'hidden') sendProgress(maxRef.current); };
    document.addEventListener('visibilitychange', onHide);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onHide); };
  }, [sendProgress]);

  const onLoaded = (e) => {
    // Reprise là où la personne s'était arrêtée (sauf tout à la fin : on repart du début pour revoir).
    const el = e.currentTarget;
    if (maxRef.current > 0 && maxRef.current < el.duration - 5) el.currentTime = maxRef.current;
  };
  const onTimeUpdate = (e) => {
    const t = e.currentTarget.currentTime;
    if (t > maxRef.current && t - maxRef.current <= CONTINUOUS_STEP_SEC) maxRef.current = t;
  };
  const onSeeking = (e) => {
    const el = e.currentTarget;
    if (el.currentTime > maxRef.current + 1) el.currentTime = maxRef.current;
  };
  const onRateChange = (e) => { if (e.currentTarget.playbackRate !== 1) e.currentTarget.playbackRate = 1; };
  const onEnded = (e) => {
    maxRef.current = Math.max(maxRef.current, e.currentTarget.duration);
    sendProgress(maxRef.current);
  };

  const next = videos.find((v) => !v.completed && v.key !== currentKey);
  const pct = current ? Math.min(100, Math.round(((current.maxWatchedSec || 0) / (current.durationSec || 1)) * 100)) : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'var(--ui-color-bg-page, #f4f7f8)', py: { xs: 3, md: 6 }, px: 2 }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="overline" sx={{ color: 'var(--ui-color-primary, #00b797)', fontWeight: 700, letterSpacing: 1.5 }}>
              Avant de commencer
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--ui-color-text, #002f36)' }}>
              Vidéos à voir
            </Typography>
            <Typography sx={{ color: 'var(--ui-color-text-muted, #5e6f74)', mt: 0.5, maxWidth: 720 }}>
              Regardez ces vidéos jusqu’au bout pour accéder à opé. Vous pouvez faire pause et revenir en arrière :
              votre progression est enregistrée, même si vous fermez l’onglet.
            </Typography>
          </Box>
          <Button variant="text" color="inherit" startIcon={<LogOut size={16} />} onClick={onLogout} sx={{ flexShrink: 0 }}>
            Se déconnecter
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
          <Box sx={{ flex: 1, width: '100%' }}>
            <Box sx={{ position: 'relative', bgcolor: '#001f23', borderRadius: 3, overflow: 'hidden', aspectRatio: '16 / 9' }}>
              {url ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption -- légendes incrustées dans la vidéo
                <video
                  key={url}
                  ref={videoRef}
                  src={url}
                  controls
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  playsInline
                  onLoadedMetadata={onLoaded}
                  onTimeUpdate={onTimeUpdate}
                  onSeeking={onSeeking}
                  onRateChange={onRateChange}
                  onPause={() => sendProgress(maxRef.current)}
                  onEnded={onEnded}
                  style={{ width: '100%', height: '100%', display: 'block' }}
                />
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0, color: '#fff' }}>
                  {error ? <Typography>{error}</Typography> : <CircularProgress color="inherit" />}
                </Stack>
              )}
            </Box>
            {current && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--ui-color-text, #002f36)' }}>{current.title}</Typography>
                <Typography sx={{ color: 'var(--ui-color-text-muted, #5e6f74)' }}>{current.description}</Typography>
                <Typography variant="body2" sx={{ mt: 1, color: current.completed ? 'var(--ui-color-primary, #00b797)' : 'var(--ui-color-text-muted, #5e6f74)', fontWeight: 600 }}>
                  {current.completed ? 'Vidéo vue' : `Progression enregistrée : ${pct} %`}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
            <Stack spacing={1.5}>
              {videos.map((v, i) => {
                const reachable = v.completed || v.key === firstPending?.key || v.key === currentKey;
                const active = v.key === currentKey;
                return (
                  <Card
                    key={v.key}
                    variant="outlined"
                    sx={{ borderRadius: 2.5, borderColor: active ? 'var(--ui-color-primary, #00b797)' : 'var(--ui-color-border, #dde3e8)', borderWidth: active ? 2 : 1, opacity: reachable ? 1 : 0.6 }}
                  >
                    <CardActionArea disabled={!reachable || active} onClick={() => setCurrentKey(v.key)} sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {v.completed && <CheckCircle2 size={22} color="#00b797" />}
                        {!v.completed && reachable && <PlayCircle size={22} color="#007773" />}
                        {!v.completed && !reachable && <Lock size={20} color="#8a9ba0" />}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: 'var(--ui-color-text-muted, #5e6f74)' }}>
                            Vidéo {i + 1} · {fmt(v.durationSec)}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, lineHeight: 1.3, color: 'var(--ui-color-text, #002f36)' }}>{v.title}</Typography>
                        </Box>
                      </Stack>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>

            <Box sx={{ mt: 3 }}>
              {allDone ? (
                <Button fullWidth variant="contained" size="large" onClick={onDone} sx={{ bgcolor: 'var(--ui-color-primary, #00b797)', fontWeight: 700, borderRadius: 2.5, py: 1.4 }}>
                  Accéder à opé
                </Button>
              ) : (
                <>
                  {current?.completed && next && (
                    <Button fullWidth variant="contained" size="large" onClick={() => setCurrentKey(next.key)} sx={{ bgcolor: 'var(--ui-color-primary, #00b797)', fontWeight: 700, borderRadius: 2.5, py: 1.4, mb: 1.5 }}>
                      Vidéo suivante
                    </Button>
                  )}
                  <Chip
                    label={`${videos.filter((v) => v.completed).length} sur ${videos.length} vue${videos.filter((v) => v.completed).length > 1 ? 's' : ''}`}
                    sx={{ fontWeight: 600 }}
                  />
                </>
              )}
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default RequiredVideosGate;
